<template>
	<AsiCard :no-bottom-margin="noBottomMargin" :title="$t('cartPosition.plural')" unwrapped class="overflow-hidden">
		<template v-slot:headerActions>
			<v-btn-toggle v-model="amountsInclVat" mandatory>
				<AsiBtn :value="true" small :color="amountsInclVat ? 'primary' : undefined" :class="{'white--text': amountsInclVat}">
					{{ $t('ui.terms.inclVat') }}
				</AsiBtn>
				<AsiBtn :value="false" small :color="!amountsInclVat ? 'primary' : undefined" :class="{'white--text': !amountsInclVat}">
					{{ $t('ui.terms.exclVat') }}
				</AsiBtn>
			</v-btn-toggle>
		</template>

		<v-divider/>
		<v-data-table
			:headers="createHeaders()"
			:items="positionsFinal"
			:options.sync="options"
			:custom-sort="sortEntries"
			:expanded.sync="expanded"
			hide-default-footer
			disable-pagination
			item-key="id"
			@click:row="showItem"
		>

			<template v-slot:item.quantity="{item}">
				<span v-if="item.quantity !== null">{{ item.quantity }}</span>
				<span v-if="itemSalesUnitOfMeasure(item) !== null" class="grey--text">
					{{ unitLabel(itemSalesUnitOfMeasure(item)) }}
				</span>
			</template>

			<template v-slot:item.item="{item}">
				<div>
					<strong>{{ item.title }}</strong>
					<span v-if="item.frameAssembly !== null"> ({{ $t('frameAssembly.assembled') }})</span>
				</div>
				<div v-if="item.subtitle !== null" class="grey--text mt-n1">
					<small>{{ item.subtitle }}</small>
				</div>
			</template>

			<template v-slot:item.itemNumber="{item}">
				<ItemChipNumber small v-if="item.itemNumber !== null" :item="item" />
			</template>

			<template v-slot:item.itemAltNumber="{item}">
				<ItemChipAltNumber small v-if="item.alternativeItemNumber !== null" :item="item" />
			</template>

			<template v-slot:item.pricePerUnit="{item}">
				<span v-if="item.price !== null && item.quantity !== null && item.frameAssembly == null" class="grey--text text--darken-1">
					{{
						$n(finalAmount(item.price), {
							key: 'currencyDisplay',
							currency: item.price.currency.currencyCode,
						})
					}}
				</span>
			</template>

			<template v-slot:item.price="{item}">
				<span v-if="item.price !== null && item.frameAssembly == null">
					{{
						$n((finalAmount(item.price) * (item.quantity === null ? 1 : item.quantity)), {
							key: 'currencyDisplay',
							currency: item.price.currency.currencyCode
						})
					}}
				</span>
				<span v-if="item.price !== null && item.frameAssembly != null">
					{{
						$n((finalAmount(item.price)), {
							key: 'currencyDisplay',
							currency: item.price.currency.currencyCode
						})
					}}
				</span>
			</template>

			<template v-slot:expanded-item="{ headers, item }">
				<td colspan="2"></td>
				<td class="px-0" colspan="5" :style="{ width: sMobile ? '100%' : '' }">
						<div v-if="item.frameAssembly != null">
							<v-data-table
								:headers="createFrameAssemblyHeaders()"
								:items="item.frameAssembly"
								hide-default-footer
								disable-pagination
								sort-by="frameType"
								sort-desc
								item-key="id"
							>

								<template v-slot:item.item="{item}">
									<div>
										<strong>{{ item.title }}</strong>
									</div>
									<div v-if="item.positionNr !== null" class="grey--text mt-n1">
										<small>{{ item.positionNr }}</small>
									</div>
								</template>

								<template v-slot:item.frameType="{item}">
									<span class="grey--text text--darken-1">
										{{ frameTypeLabel(item.frameType) }}
									</span>
								</template>

								<template v-slot:item.installation="{item}">
									<span class="grey--text text--darken-1">
										{{ installationLabel(item.installation) }}
									</span>
								</template>

								<template v-slot:item.measuringPoint="{item}">
									<span class="grey--text text--darken-1">
										{{ measuringPointLabel(item.measuringPoint) }}
									</span>
								</template>

								<template v-slot:item.width="{item}">
									<span class="grey--text text--darken-1">
										{{ item.width }}
									</span>
								</template>

								<template v-slot:item.height="{item}">
									<span class="grey--text text--darken-1">
										{{ item.height }}
									</span>
								</template>

								<template v-slot:item.quantity="{item}">
									<span v-if="item.quantity !== null">
										{{ item.quantity }}
										<span class="grey--text">
											{{ unitLabel(itemSalesUnitOfMeasure(item)) }}
										</span>
									</span>
								</template>

								<template v-slot:item.pricePerUnit="{item}">
									<span v-if="(item.price !== null && item.quantity !== null) && (item.width != null && item.height != null)" class="grey--text text--darken-1">
										{{
											$n(finalAmount(item.price) / item.totalFrameLength, {
												key: 'currencyDisplay',
												currency: item.price.currency.currencyCode,
											})
										}}
									</span>
									<span v-if="(item.price !== null && item.quantity !== null) && (item.width == null && item.height == null)" class="grey--text text--darken-1">
										{{
											$n(finalAmount(item.price) / item.quantity, {
												key: 'currencyDisplay',
												currency: item.price.currency.currencyCode,
											})
										}}
									</span>
								</template>

								<template v-slot:item.price="{item}">
									<span v-if="item.price !== null">
										{{
											$n((finalAmount(item.price)), {
												key: 'currencyDisplay',
												currency: item.price.currency.currencyCode
											})
										}}
									</span>
								</template>

							</v-data-table>
						</div>
				</td>
			</template>

			<template v-slot:body.append>
				<tr class="grey lighten-5">
					<td colspan="6" class="font-weight-bold">
						{{ $t('ui.terms.total') }}
					</td>
					<td :style="{ width: sMobile ? '100%' : '' }">
						<div v-for="total in (amountsInclVat ? totalEntriesInclVat : totalEntriesExclVat)" :key="total.currency.currencyCode" class="d-flex flex-column align-end">
							<span class="font-weight-bold">
								{{
									$n(total.amount, {
										key: 'currencyDisplay',
										currency: total.currency.currencyCode,
									})
								}}
							</span>
						</div>
						<div class="text-right">
							<small>{{ $t(amountsInclVat ? 'ui.terms.inclVat' : 'ui.terms.exclVat') }}</small>
						</div>
					</td>
				</tr>
			</template>
		</v-data-table>

		<template v-slot:actions>
			<AsiBtn v-if="reorder" color="primary" @click="execReorder" :block="sMobile">{{
					$t('order.terms.reorder')
				}}
			</AsiBtn>
		</template>
	</AsiCard>
</template>

<script lang="ts">
	import {Component, Mixins, Prop} from 'vue-property-decorator';
	import {IOrderShop} from "@/models/order/OrderShopModels";
	import AsiListTableHeader from "@/components/common/AsiListTableHeader";
	import AsiListTableOptions from "@/components/common/AsiListTableOptions";
	import {
		FrameAssemblyInstallation,
		FrameAssemblyMeasuringPoint,
		FrameAssemblyType,
		UnitOfMeasure
	} from "@/helpers/constants";
	import VueI18n from "vue-i18n";
	import EnumHelper from "@/helpers/EnumHelper";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import AsiCard from "@/components/common/AsiCard.vue";
	import AsiAvatar from "@/components/common/AsiAvatar.vue";
	import Icon from "@/plugins/icons";
	import {IPrice} from "@/models/price/PriceModels";
	import TotalHelper, {CurrencyAmount} from "@/helpers/TotalHelper";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import ResponsiveChecks from "@/mixins/ResponsiveChecks.vue";
	import ItemHelper from "@/models/item/ItemHelper";
	import {
		IFrameAssembly,
		IFrameAssemblyConfiguration,
	} from "@/models/frame-assembly/FrameAssemblyModels";
	import CartHelper from "@/helpers/CartHelper";
	import TranslateResult = VueI18n.TranslateResult;
	import ItemChipNumber from "@/components/item/ItemChipNumber.vue";
	import ItemChipAltNumber from "@/components/item/ItemChipAltNumber.vue";

	class PositionEntry {
		public id: string | null;
		public title!: string | TranslateResult;
		public subtitle: string | TranslateResult | null = null;
		public price: IPrice | null = null;
		public trimmingCost: IPrice | null = null;
		public positionIndex: number | null = null;
		public quantity: number | null = null;
		public salesUnitOfMeasure: UnitOfMeasure | null = null;
		public frameAssembly: FrameAssemblyPositionEntry[] | null = null;
		public itemNumber: string | null = null;
		public alternativeItemNumber: string | null = null

		public constructor(id: string | null, title: string | TranslateResult) {
			this.id = id;
			this.title = title;
		}
	}

	class FrameAssemblyPositionEntry {
		public id: string | null;
		public title: string | TranslateResult;
		public frameType: FrameAssemblyType | null;
		public installation: FrameAssemblyInstallation | null;
		public measuringPoint: FrameAssemblyMeasuringPoint | null;
		public width: number | null;
		public price: IPrice | null = null;
		public height: number | null;
		public totalFrameLength: number | null;
		public positionNr: string | null = null;
		public quantity: number | null;
		public salesUnitOfMeasure: UnitOfMeasure | null = null;

		public constructor(id: string | null,
		                   title: string | TranslateResult,
		                   frameType: FrameAssemblyType | null,
						   installation: FrameAssemblyInstallation | null,
						   measuringPoint: FrameAssemblyMeasuringPoint | null,
						   quantity: number | null,
		                   frameAssemblyConfiguration: IFrameAssemblyConfiguration | null,
		                   price: IPrice) {
			this.id = id;
			this.title = title;
			this.price = price;
			this.frameType = frameType;
			this.installation = installation;
			this.measuringPoint = measuringPoint;
			this.quantity = quantity;
			this.width = frameAssemblyConfiguration?.width ?? null;
			this.height = frameAssemblyConfiguration?.height ?? null;
			this.positionNr = frameAssemblyConfiguration?.positionNr ?? null;
			this.totalFrameLength = frameAssemblyConfiguration?.totalFrameLength ?? null;
		}
	}

	@Component({
		components: {ItemChipAltNumber, ItemChipNumber, AsiBtn, AsiAvatar, AsiCard}
	})
	export default class OrderCardPositions extends Mixins(ResponsiveChecks) {

		@Prop({type: Object, required: true})
		public order!: IOrderShop;

		@Prop({type: Boolean, default: false})
		public reorder!: boolean;

		@Prop({type: Boolean, default: false})
		public noBottomMargin!: boolean;

		private get expanded(){
			return this.positionsFinal
				.filter(p => p.frameAssembly != null);
		}

		private icons = Icon;
		private options: AsiListTableOptions = new AsiListTableOptions();

		public created(): void {
			this.options.sortBy = ['positionIndex'];
			this.options.sortDesc = [false];
		}

		private get amountsInclVat(): boolean {
			return this.$store.state.ui.amountsInclVat;
		}

		private set amountsInclVat(value: boolean) {
			this.$store.commit('ui/setAmountsInclVat', value);
		}

		private get totalEntriesExclVat(): CurrencyAmount[] {
			return TotalHelper.totalExclVat(this.order, true);
		}

		private get totalEntriesInclVat(): CurrencyAmount[] {
			return TotalHelper.totalInclVat(this.order, true);
		}

		private get positionsFinal(): PositionEntry[] {
			const positions: PositionEntry[] = this.order.positions.map(p => {
				const entry = new PositionEntry(p.id, TranslatedValueHelper.get(p.item.name, this.$i18n.locale, true) ?? '?');
				entry.subtitle = p.customerReference;
				entry.price = p.price;
				entry.trimmingCost = p.trimmingCost;
				entry.positionIndex = p.positionIndex;
				entry.quantity = p.quantity;
				entry.salesUnitOfMeasure = p.item.salesUnitOfMeasure;
				entry.frameAssembly = p.frameAssembly != null ? this.getFrameAssemblyPositionEntriesFromModel(p.frameAssembly) : null;
				entry.itemNumber = p.item.itemNumber;
				entry.alternativeItemNumber = p.item.alternativeItemNumber;

				return entry;
			});

			// create trimming cost summary position
			const trimmingCostPrices = (this.order.positions
				.map(pos => pos.trimmingCost)
				.filter(tc => tc !== null) as IPrice[]);

			if (trimmingCostPrices.length > 0) {
				const totalAmount = trimmingCostPrices.map(tc => tc.amount).reduce((acc: number, cur: number) => acc + cur, 0);

				const qty = trimmingCostPrices.length;

				const trimmingCostSummaryPosition = new PositionEntry(null, this.$t('globalConfig.terms.trimmingCost'));
				trimmingCostSummaryPosition.price = {
					vat: trimmingCostPrices[0].vat,
					currency: trimmingCostPrices[0].currency,
					amount: totalAmount / qty,
				} as IPrice;
				trimmingCostSummaryPosition.quantity = qty;
				positions.push(trimmingCostSummaryPosition);
			}

			if (this.order.promotionalItem !== null) {
				const promoEntry = new PositionEntry('promotionalItem', this.$t('item.promotionalItem.singular'));
				promoEntry.subtitle = TranslatedValueHelper.get(this.order.promotionalItem.item.name, this.$i18n.locale, true);
				promoEntry.quantity = 1;
				positions.push(promoEntry);
			}

			if (this.order.minimumOrderSurcharge !== null) {
				const surchargeEntry = new PositionEntry('minimumOrderSurcharge', this.$t('cart.minimumOrderSurcharge'));
				surchargeEntry.price = this.order.minimumOrderSurcharge.price;
				surchargeEntry.quantity = 1;
				positions.push(surchargeEntry);
			}

			if (this.order.bulkyGoodsSurcharge !== null) {
				const surchargeEntry = new PositionEntry('bulkyGoodsSurcharge', this.$t('cart.bulkyGoodsSurcharge'));
				surchargeEntry.price = this.order.bulkyGoodsSurcharge.price;
				surchargeEntry.quantity = 1;
				positions.push(surchargeEntry);
			}

			const postageEntry = new PositionEntry('postage', this.$t('shipmentType.postage'));
			postageEntry.subtitle = TranslatedValueHelper.get(this.order.shipmentType.name, this.$i18n.locale, true);
			postageEntry.price = this.order.shipmentType.postage;
			positions.push(postageEntry);

			return positions;
		}

		private getFrameAssemblyPositionEntriesFromModel(frameAssembly: IFrameAssembly){
			const positions: FrameAssemblyPositionEntry[] = [];
			positions.push(new FrameAssemblyPositionEntry(frameAssembly.id, this.$t('frameAssembly.setupCosts'), null, null, null, null, null, frameAssembly.setupPrice));

			frameAssembly.frameAssemblyConfigurationGroups.forEach(cg => {
				positions.push(new FrameAssemblyPositionEntry(null, this.$t('frameAssembly.sealCosts'), cg.frameType, null, null, cg.quantity, null, {
					amount: cg.sealPrice.amount * cg.quantity,
					currency: cg.sealPrice.currency,
					vat: cg.sealPrice.vat
				}));

				cg.frameAssemblyConfigurations.forEach(c => {
					positions.push(new FrameAssemblyPositionEntry(c.id, this.$t('frameAssembly.singular'), cg.frameType, c.installation, c.measuringPoint, c.quantity, c, c.price));
				});
			});

			return positions;
		}

		private createHeaders(): AsiListTableHeader[] {
			return [
				new AsiListTableHeader(this.$t('cartPosition.positionIndex'), 'positionIndex', false, false, 'center'),
				new AsiListTableHeader(this.$t('cartPosition.quantity'), 'quantity', false, false, 'right'),
				new AsiListTableHeader(this.$t('item.singular'), 'item', false, false, 'left'),
				new AsiListTableHeader(this.$t('item.itemNumber'), 'itemNumber', false, false, 'left', '3rem'),
				new AsiListTableHeader(this.$t('item.alternativeItemNumber'), 'itemAltNumber', false, false, 'left', '3rem'),
				new AsiListTableHeader(this.$t('item.unitPrice'), 'pricePerUnit', false, false, 'right', '10rem'),
				new AsiListTableHeader(this.$t('item.price'), 'price', false, false, 'right', '10rem'),
			];
		}

		private createFrameAssemblyHeaders(): AsiListTableHeader[] {
			return [
				new AsiListTableHeader(this.$t('item.singular'), 'item', false, false, 'left'),
				new AsiListTableHeader(this.$t('cartPosition.quantity'), 'quantity', false, false, 'left', '8rem'),
				new AsiListTableHeader(this.$t('frameAssembly.frameType'), 'frameType', false, false, 'left', '8rem'),
				new AsiListTableHeader(this.$t('frameAssembly.installation'), 'installation', false, false, 'left', '10rem'),
				new AsiListTableHeader(this.$t('frameAssembly.measuringPoint'), 'measuringPoint', false, false, 'left', '10rem'),
				new AsiListTableHeader(this.$t('frameAssembly.width'), 'width', false, false, 'right', '10rem'),
				new AsiListTableHeader(this.$t('frameAssembly.height'), 'height', false, false, 'right', '10rem'),
				new AsiListTableHeader(this.$t('item.unitPrice'), 'pricePerUnit', false, false, 'right', '10rem'),
				new AsiListTableHeader(this.$t('item.price'), 'price', false, false, 'right', '10rem'),
			];
		}

		// noinspection JSMethodCanBeStatic
		private unitLabel(unit: UnitOfMeasure): string | TranslateResult | null {
			return EnumHelper.textFromValue(UnitOfMeasure, unit, true);
		}

		private frameTypeLabel(frameType: FrameAssemblyType): string | null {
			return EnumHelper.textFromValue(FrameAssemblyType, frameType, true);
		}

		private installationLabel(installation: FrameAssemblyInstallation): string | null {
			return EnumHelper.textFromValue(FrameAssemblyInstallation, installation, true);
		}

		private measuringPointLabel(measuringPoint: FrameAssemblyMeasuringPoint): string | null {
			return EnumHelper.textFromValue(FrameAssemblyMeasuringPoint, measuringPoint, true);
		}

		private sortEntries(entries: PositionEntry[], sortBy: string[], sortDesc: boolean[]): PositionEntry[] {
			const column = sortBy.length > 0 ? sortBy[0] : 'user';
			const desc = sortDesc.length > 0 ? sortDesc[0] : true;

			return entries.sort((a: PositionEntry, b: PositionEntry) => {
				if (a.positionIndex === null && b.positionIndex === null) return 0;
				if (a.positionIndex === null) return 1;
				if (b.positionIndex === null) return -1;

				switch (column) {
					case 'item':
						return a.title.toString().localeCompare(b.title.toString()) * (desc ? -1 : 1);
					default:
						//@ts-ignore
						return desc ? (b[column] < a[column] ? -1 : 1) : (a[column] < b[column] ? -1 : 1);
				}
			});
		}

		private finalAmount(price: IPrice): number {
			return price.amount + (this.amountsInclVat ? TotalHelper.vatAmount(price) : 0);
		}

		private itemSalesUnitOfMeasure(position: PositionEntry): UnitOfMeasure | null {
			return position.frameAssembly !== null ? UnitOfMeasure.unitPiece : position.salesUnitOfMeasure;
		}

		private showItem(positionEntry: PositionEntry): void {
			let position = this.order.positions
				.find(position => position.id === positionEntry.id);

			let itemNumber = position !== undefined ? position.item.itemNumber : null;

			if (itemNumber === null) return;
			this.$router.push(ItemHelper.detailRoute(itemNumber));
		}

		private execReorder(): void {
			this.$orderServiceShop.reorder(this.order.id)
				.then(res => {
					this.$store.commit('cart/setActiveCartId', res.id);
					this.$store.dispatch('cart/loadCarts');
					this.$router.push(CartHelper.checkoutRoute(res.id));
				})
				.catch(() => console.error('reorder failed'));
		}

	}
</script>

<style lang="scss" scoped>
	::v-deep .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
		box-shadow: none !important;
	}

	.frame-assembly-wrapper{
		width: 90%;
	}
</style>
